/* $: yarn add -D @types/gtag.js */

import React, { useEffect } from 'react'
import { globalHistory } from '@reach/router'
import { useRecoilState } from 'recoil'

import { userActivityState } from 'src/recoil/atoms'

const GA_UA_CODE = process.env.GATSBY_GA_UA_CODE

interface eventProps {
  action: string
  category: string
  label: string
  value?: any
}
interface viewProps {
  title: string
  location: string
  path: string
  dimension1?: string
}
interface searchProps {
  search_term: string
  search_results?: number
}

interface selectItemProps {
  item_id?: string
  item_name?: string
  price?: number | string
  item_list_name?: string
  item_list_id?: string
  search_term?: string
  index?: number
}

interface selectContentProps {
  content_type: string
  content_id: string
  search_term?: string
}

interface fileDownloadProps {
  file_name: string
  item_id?: string
  item_name?: string
  link_text?: string
}

interface wishlistProps {
  value: number | string
  item: { item_id: string; item_name: string; price: number | string }
}

let lastTrackedSearch = { term: '', at: 0 }

declare global {
  interface Window {
    gtag: any
  }
}

export default function GoogleAnalytics() {
  const [, setUserActivity] = useRecoilState(userActivityState)
  useEffect(() => {
    const pageView = (url: string) => {
      if (window && window.gtag) {
        window.gtag('set', 'page_path', url)
        window.gtag('event', 'page_view')
      }
    }

    const handleRouteChange = (url: string) => {
      // Register page view to anlaytics.
      pageView(url)
      // Reset user activity event data
      console.log('reset user activity')
      setUserActivity({ hasScrolled: false, hasStayed: false })
    }
    const removeListener = globalHistory.listen((params) => {
      const { location } = params
      if (location?.pathname) {
        handleRouteChange(location.pathname)
      }
    })
    return () => {
      removeListener()
    }
  }, [globalHistory.listen])

  return <div />
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const gtagEvent = ({ action, category, label, value }: eventProps) => {
  if (window && window.gtag) {
    window.gtag('event', category === 'Click - Buy' ? category : action, {
      event_category: category,
      event_action: action,
      event_label: label,
      value: value,
    })
  }
}

// https://developers.google.com/analytics/devguides/collection/gtagjs/events
export const gtagUAPageView = ({
  title,
  location,
  path,
  dimension1,
}: viewProps) => {
  if (window && window.gtag && GA_UA_CODE) {
    window.gtag('event', 'page_view', {
      page_title: title,
      page_location: location,
      page_path: path,
      dimension1: dimension1,
      send_to: GA_UA_CODE,
    })
  }
}

export const gtagGA4Search = ({ search_term, search_results }: searchProps) => {
  const term = search_term?.trim()
  if (!term || term.length < 2 || !(window && window.gtag)) {
    return
  }

  const now = Date.now()
  if (lastTrackedSearch.term === term && now - lastTrackedSearch.at < 4000) {
    return
  }
  lastTrackedSearch = { term, at: now }

  window.gtag('event', 'search', {
    search_term: term,
    search_results,
  })
}

export const gtagSelectItem = ({
  item_id,
  item_name,
  price,
  item_list_name,
  item_list_id,
  search_term,
  index,
}: selectItemProps) => {
  if (!(window && window.gtag) || !item_name) {
    return
  }

  const numericPrice = price ? Number(price) : undefined

  window.gtag('event', 'select_item', {
    item_list_id: item_list_id || item_list_name,
    item_list_name: item_list_name,
    search_term: search_term || undefined,
    items: [
      {
        item_id: item_id || item_name,
        item_name,
        price: numericPrice,
        index,
        quantity: 1,
      },
    ],
  })
}

export const gtagSelectContent = ({
  content_type,
  content_id,
  search_term,
}: selectContentProps) => {
  if (!(window && window.gtag) || !content_id) {
    return
  }

  window.gtag('event', 'select_content', {
    content_type,
    content_id,
    search_term: search_term || undefined,
  })
}

export const gtagFileDownload = ({
  file_name,
  item_id,
  item_name,
  link_text,
}: fileDownloadProps) => {
  if (!(window && window.gtag)) {
    return
  }

  window.gtag('event', 'file_download', {
    file_name,
    file_extension: 'print',
    link_text: link_text || file_name,
    item_id,
    item_name,
  })
}

// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtag#add_to_wishlist
export const gtagAddToWishlist = ({ item, value }: wishlistProps) => {
  if (window && window.gtag) {
    window.gtag('event', 'add_to_wishlist', {
      currency: 'SEK',
      value: value,
      items: [
        {
          ...item,
          quantity: 1,
          // affiliation: 'Google Merchandise Store',
          // coupon: 'SUMMER_FUN',
          // currency: 'USD',
          // discount: 2.22,
          // index: 0,
          // item_brand: 'Google',
          // item_category: 'Apparel',
          // item_category2: 'Adult',
          // item_category3: 'Shirts',
          // item_category4: 'Crew',
          // item_category5: 'Short sleeve',
          // item_list_id: 'related_products',
          // item_list_name: 'Related Products',
          // item_variant: 'green',
          // location_id: 'L_12345',
        },
      ],
    })
  }
}
